/**
 * OsActionReport — the response dialect every os_* actuation returns (P1).
 *
 * Mirrors BrowserActionReport (packages/shared/src/computer-use/action-report.ts)
 * but is deliberately self-contained: the OS-hands leg imports nothing from the
 * browser leg (lane hygiene). The report is the sidecar's ground-truth
 * self-description of what it just did on the Mac. `rung` (which ladder step
 * actually actuated), `mode` (how the target was addressed), `target` (the AX
 * ref / role / label), and `app` (bundle id) are recorded on the actuator side
 * of the trust boundary, never narrated by the model. `risk`/`riskReasons` are
 * machine-derived (e.g. 'label_less_target'), not free text — an injected brain
 * can request a dangerous action but cannot forge a report that calls it safe.
 * `effect` states what the sidecar could actually observe: 'confirmed' (intended
 * consequence seen), 'unverifiable' (acted, could not check), or 'suspected_noop'
 * (acted, the target looks untouched).
 */

import { classifyOsVerbRisk } from './os-verbs.js';

export const OS_HANDS_RISK_TIERS = ['low', 'medium', 'high'] as const;
export type OsHandsRiskTier = (typeof OS_HANDS_RISK_TIERS)[number];

export const OS_ACTION_EFFECTS = ['confirmed', 'unverifiable', 'suspected_noop'] as const;
export type OsActionEffect = (typeof OS_ACTION_EFFECTS)[number];

/** How the target was addressed: an AX ref, the focused element (rung-2 CGEvent
 *  with no explicit ref), an app-level op (apps/open), or nothing (read ops). */
export const OS_ACTION_MODES = ['ref', 'focused', 'app', 'none'] as const;
export type OsActionMode = (typeof OS_ACTION_MODES)[number];

/** Which rung of the determinism ladder actually actuated:
 *  0 = Tier-0 verb (deterministic app-function call via VerbEngine — never the
 *  AX tree), 1 = AX semantic action, 2 = pid-targeted CGEvent, 3 = takeover (P2),
 *  4 = pixels (P2). P1 never emits 3 or 4 — a rung-≥3 need returns
 *  TAKEOVER_REQUIRED instead — but the type carries them so the report
 *  vocabulary stays stable across phases. */
export const OS_ACTION_RUNGS = [0, 1, 2, 3, 4] as const;
export type OsActionRung = (typeof OS_ACTION_RUNGS)[number];

export interface OsActionTarget {
  ref?: string;
  role?: string;
  label?: string;
}

export interface OsActionReport {
  v: 1;
  tool: string; // short tool name, e.g. 'os_click'
  rung: OsActionRung;
  mode: OsActionMode;
  target?: OsActionTarget;
  app?: string; // bundle id of the driven app at action time (ground truth)
  latencyMs: number;
  verified: boolean;
  effect: OsActionEffect;
  risk: OsHandsRiskTier;
  riskReasons: string[]; // machine-checkable reasons, e.g. ['label_less_target']
}

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

function isOneOf<T extends string | number>(members: readonly T[], v: unknown): v is T {
  return (members as readonly unknown[]).includes(v);
}

function isOsActionTarget(v: unknown): v is OsActionTarget {
  return (
    isRecord(v) &&
    (v.ref === undefined || typeof v.ref === 'string') &&
    (v.role === undefined || typeof v.role === 'string') &&
    (v.label === undefined || typeof v.label === 'string')
  );
}

export function isOsActionReport(v: unknown): v is OsActionReport {
  return (
    isRecord(v) &&
    v.v === 1 &&
    typeof v.tool === 'string' &&
    isOneOf(OS_ACTION_RUNGS, v.rung) &&
    isOneOf(OS_ACTION_MODES, v.mode) &&
    (v.target === undefined || isOsActionTarget(v.target)) &&
    (v.app === undefined || typeof v.app === 'string') &&
    typeof v.latencyMs === 'number' &&
    typeof v.verified === 'boolean' &&
    isOneOf(OS_ACTION_EFFECTS, v.effect) &&
    isOneOf(OS_HANDS_RISK_TIERS, v.risk) &&
    Array.isArray(v.riskReasons) &&
    v.riskReasons.every((reason) => typeof reason === 'string')
  );
}

// ── Structural risk classifier ────────────────────────────────────────────────
// The machine-derived risk table: a pure function of (tool, mode, target, app).
// The default row is high/'unclassified_tool', so a new or mis-routed os_* tool
// can never slip under the gate. It lives in @bash/shared so the SERVER can
// classify risk itself from ground-truth fields and never has to trust a
// client-sent `risk` — an injected brain can request a dangerous action but
// cannot forge a report that calls it safe.

const READ_TOOLS = new Set(['os_apps', 'os_snapshot', 'os_find', 'os_wait', 'os_list_verbs']);
// os_release_takeover returns the hands (restores focus) — the safe direction.
const LOW_RISK_ACTIONS = new Set(['os_scroll', 'os_release_takeover']);
const MEDIUM_RISK_ACTIONS = new Set(['os_open']);
const TARGETED_ACTIONS = new Set(['os_click', 'os_set_value', 'os_type', 'os_key', 'os_menu']);
// P2 takeover rung: os_takeover borrows the physical cursor/keyboard, os_drag
// drives real HID under that grant — structurally high, not fail-closed-for-scope.
const TAKEOVER_HID_ACTIONS_SET = new Set(['os_takeover', 'os_drag']);

/** Read-only os_* tools. Exported so the sandbox can mark a read report
 *  `verified` without re-declaring the set (the classifier treats them as low). */
export const OS_READ_TOOLS: ReadonlySet<string> = READ_TOOLS;

/** Takeover/HID os_* tools. These consult the gate ONLY via the sandbox's
 *  first-engage carrier — the per-app Ask deliberately excludes them (they are
 *  not in MUTATING_TOOLS) because the SDK-level hard-ask owns takeover consent.
 *  Exported as the single source both the classifier (structurally high) and the
 *  server gate (short-circuit past the per-app matrix so no THIRD card stacks on
 *  the hard-ask) key off — so the two never drift. */
export const TAKEOVER_HID_ACTIONS: ReadonlySet<string> = TAKEOVER_HID_ACTIONS_SET;

/** Pixel-capture os_* tools. These consult the gate on the 'pixels' consent
 *  LANE (never 'actuation') — the sandbox keys its per-app Ask off this set, the
 *  server binds the lane to it, and the classifier's os_screenshot/os_ocr
 *  special cases key on the same names. Single source so the three never drift. */
export const OS_PIXEL_TOOLS: ReadonlySet<string> = new Set(['os_screenshot', 'os_ocr']);

export function classifyOsStructuralRisk(input: {
  tool: string;
  mode: OsActionMode;
  target?: { ref?: string; role?: string; label?: string };
  app?: string;
}): { risk: OsHandsRiskTier; riskReasons: string[] } {
  const { tool, mode, target, app } = input;
  if (READ_TOOLS.has(tool) || LOW_RISK_ACTIONS.has(tool)) return { risk: 'low', riskReasons: [] };
  if (MEDIUM_RISK_ACTIONS.has(tool)) return { risk: 'medium', riskReasons: [] };
  if (TAKEOVER_HID_ACTIONS_SET.has(tool)) return { risk: 'high', riskReasons: ['physical_takeover'] };
  // Tier-0 verb lane: risk comes from the shared registry's sensitivity,
  // resolved by the verb id the tool layer threads as target.label.
  if (tool === 'os_run_verb') {
    const verbRisk = classifyOsVerbRisk(target?.label ?? '');
    if (verbRisk === 'unknown') return { risk: 'high', riskReasons: ['unknown_verb'] };
    return { risk: verbRisk, riskReasons: verbRisk === 'high' ? ['sensitive_verb'] : [] };
  }
  // P2 pixel reads: pixels leave the AX-text-only contract, so the capture floor
  // sits above a targeted read. Scoped to a labeled target or a known app the
  // capture is auditable; label-less AND app-less is unscoped — fail closed a
  // tier up. OCR ships text only (no pixels leave the machine), one tier below.
  if (tool === 'os_screenshot') {
    return target?.label?.trim() || app
      ? { risk: 'medium', riskReasons: [] }
      : { risk: 'high', riskReasons: ['unscoped_capture'] };
  }
  if (tool === 'os_ocr') {
    return target?.label?.trim() || app
      ? { risk: 'low', riskReasons: [] }
      : { risk: 'medium', riskReasons: ['unscoped_capture'] };
  }
  if (TARGETED_ACTIONS.has(tool)) {
    // Typing into the focused element with no ref is unscoped — fail closed.
    if (mode === 'focused') return { risk: 'high', riskReasons: ['unscoped_focused_input'] };
    // ref/app modes: a label makes the action auditable; a whitespace-only or
    // missing accessible name is as unauditable as none — fail closed.
    return target?.label?.trim()
      ? { risk: 'medium', riskReasons: [] }
      : { risk: 'high', riskReasons: ['label_less_target'] };
  }
  return { risk: 'high', riskReasons: ['unclassified_tool'] }; // fail-closed floor
}

/**
 * SENSITIVE-ACTION model (App-Grant V2). Consequential, hard-to-undo controls the
 * user would want to confirm even inside an app they've already trusted: send,
 * delete, and pay/purchase (and their close cousins). Deliberately TIGHT — a loose
 * pattern would re-introduce the per-action friction this whole model exists to
 * remove — so it matches distinctive verbs/phrases, not generic words like "OK",
 * "Save", "Submit", or "Confirm" on their own. Matched against a control's
 * accessible label (case-insensitive). Extend this list, never widen it to catch-alls.
 */
const SENSITIVE_ACTION_LABEL_RE =
  /\b(?:send|post|tweet|publish|delete|discard|erase|trash|move\s+to\s+trash|empty\s+trash|delete\s+account|pay|purchase|checkout|check\s?out|buy|subscribe|(?:place|submit|complete)\s+(?:your\s+)?order|order\s+now|confirm\s+(?:payment|order|purchase|transfer|and\s+pay)|transfer|withdraw|unsubscribe|deactivate)\b/i;

/** Destructive / send keyboard chords, normalized the way the sidecar's parser is
 *  (VestraHandsCore/KeyChord.swift KeyChord.parse): lowercase, split on '+', trim
 *  each token, drop empties; the LAST token is the base key, the rest are
 *  modifiers, and cmd/command/meta all name the command key (order-insensitive).
 *  Sensitive iff a command modifier is held AND the base is delete/backspace
 *  (move to trash) or return/enter (send in Mail/Slack). Matching the parser
 *  instead of literal spellings closes the alias hole: "command+delete",
 *  "meta + delete", and "shift+cmd+delete" all actuate the same chord as
 *  "cmd+delete" and must all gate. A BARE return/enter is deliberately NOT a
 *  sensitive chord (cmd must be held) — see the v1 notes on
 *  {@link isSensitiveOsAction}. */
const CMD_MODIFIER_TOKENS = new Set(['cmd', 'command', 'meta']);
const SENSITIVE_BASE_KEYS = new Set(['delete', 'backspace', 'return', 'enter']);

function isSensitiveKeyChord(spec: string): boolean {
  const tokens = spec
    .toLowerCase()
    .split('+')
    .map((t) => t.trim())
    .filter((t) => t.length > 0);
  if (tokens.length < 2) return false; // a bare key holds no modifier
  const base = tokens[tokens.length - 1];
  return SENSITIVE_BASE_KEYS.has(base) && tokens.slice(0, -1).some((t) => CMD_MODIFIER_TOKENS.has(t));
}

/**
 * Is this actuation one of the sensitive set (send / delete / pay) that must be
 * confirmed even in an already-granted app? Best-effort + fail-closed on a MATCH:
 * a labeled control naming a sensitive verb, or a destructive key chord. A normal
 * click/type/key/scroll (a Calculator button, a text field, a menu tab) is NOT
 * sensitive — the app grant covers it. For os_key the label is EITHER the chord
 * (threaded by the tool layer when the target has no accessible name) or the
 * focused element's real label (kept when it has one), so BOTH facets are
 * checked: a destructive/send chord, and a sensitive control operated by key.
 *
 * Known v1 gaps — deliberate best-effort trades, DOCUMENTED not solved:
 *  1. English-only labels: localized controls ("Senden", "Löschen", "Pagar")
 *     ride the app grant ungated. Locale-aware sensitivity is a follow-up.
 *  2. A bare Return/Enter press (send-in-chat in many apps) is NOT gated —
 *     gating it would wreck ordinary typing; only cmd+return/enter gates.
 *  3. A label-less sensitive control (no accessible name) rides the grant —
 *     labels/chords are the only signal this heuristic sees.
 *  4. os_key on a ref whose element label is benign, carrying a destructive
 *     chord: the element label wins (it names what is acted on), so the chord
 *     is not seen — a rare accepted miss.
 *  5. The chord check requires cmd for delete/backspace too, but the LABEL
 *     pattern still catches a bare "delete" key spec (fail-closed asymmetry:
 *     bare delete removes the selected item in list apps like Mail).
 */
export function isSensitiveOsAction(input: {
  tool: string;
  target?: { ref?: string; role?: string; label?: string } | undefined;
}): boolean {
  const label = input.target?.label?.trim();
  if (!label) return false;
  if (input.tool === 'os_key') return isSensitiveKeyChord(label) || SENSITIVE_ACTION_LABEL_RE.test(label);
  return SENSITIVE_ACTION_LABEL_RE.test(label);
}

/**
 * App-Grant risk model (V2, behind OS_HANDS_APP_GRANT_V1). Same structure as
 * {@link classifyOsStructuralRisk} EXCEPT the actuation branch: once an app is
 * granted, every normal action (click/type/key/menu/set_value — labeled, focused,
 * or label-less) rides the app grant as `medium` (one per-app card, then silent).
 * ONLY a SENSITIVE action (send/delete/pay) is `high` — always-ask, even in a
 * granted app, never persisted. Reads, scroll, pixels, and takeover are unchanged
 * from V1, so the only behavior delta is: no more per-keystroke / per-click cards
 * for ordinary automation, and a real confirm on the consequential few.
 */
export function classifyOsActionRiskV2(input: {
  tool: string;
  mode: OsActionMode;
  target?: { ref?: string; role?: string; label?: string };
  app?: string;
}): { risk: OsHandsRiskTier; riskReasons: string[] } {
  const { tool, target, app } = input;
  if (READ_TOOLS.has(tool) || LOW_RISK_ACTIONS.has(tool)) return { risk: 'low', riskReasons: [] };
  if (MEDIUM_RISK_ACTIONS.has(tool)) return { risk: 'medium', riskReasons: [] };
  if (TAKEOVER_HID_ACTIONS_SET.has(tool)) return { risk: 'high', riskReasons: ['physical_takeover'] };
  // Tier-0 verb lane: risk comes from the shared registry's sensitivity,
  // resolved by the verb id the tool layer threads as target.label.
  if (tool === 'os_run_verb') {
    const verbRisk = classifyOsVerbRisk(target?.label ?? '');
    if (verbRisk === 'unknown') return { risk: 'high', riskReasons: ['unknown_verb'] };
    return { risk: verbRisk, riskReasons: verbRisk === 'high' ? ['sensitive_verb'] : [] };
  }
  // Pixel floor: unchanged from V1 (capture auditability is orthogonal to the app grant).
  if (tool === 'os_screenshot') {
    return target?.label?.trim() || app
      ? { risk: 'medium', riskReasons: [] }
      : { risk: 'high', riskReasons: ['unscoped_capture'] };
  }
  if (tool === 'os_ocr') {
    return target?.label?.trim() || app
      ? { risk: 'low', riskReasons: [] }
      : { risk: 'medium', riskReasons: ['unscoped_capture'] };
  }
  if (TARGETED_ACTIONS.has(tool)) {
    return isSensitiveOsAction({ tool, target })
      ? { risk: 'high', riskReasons: ['sensitive_action'] }
      : { risk: 'medium', riskReasons: [] }; // rides the per-app grant — one card, then silent
  }
  return { risk: 'high', riskReasons: ['unclassified_tool'] }; // fail-closed floor
}
